import React, { useState } from "react";
import ReactMarkdown from "react-markdown";
import { useTranslation } from "react-i18next";
import { MapPin, Maximize2, Minimize2 } from "lucide-react";
import { Badge } from "../ui/Badge";
import {
  MARKDOWN_COMPONENTS,
  safeImageUrl,
  urlTransform,
} from "../announcements/RemoteAnnouncementContent";
import { commerceMenuLabelOf } from "./commerceMenu";
import { NEWS_CATEGORIES } from "./newsRules";

// ─────────────────────────────────────────────────────────────────────────────
// Contenido de una novedad: categoría, fecha, "dónde está", imagen, texto y
// pasos. Lo usan el detalle del portal y la vista previa del editor, para que
// Beartrack vea EXACTAMENTE lo que verá el comercio.
//
// No dice quién habla: la marca va en la cabecera del panel (la del operador
// cuando el comercio la ve en su portal). Por eso el texto no debe nombrar a
// Beartrack.
// ─────────────────────────────────────────────────────────────────────────────

export function formatNewsDate(iso, locale) {
  if (!iso) return "";
  try {
    return new Intl.DateTimeFormat(locale, { dateStyle: "medium" }).format(new Date(iso));
  } catch {
    return "";
  }
}

export function CategoryBadge({ category }) {
  const { t } = useTranslation();
  const meta = NEWS_CATEGORIES[category] ?? NEWS_CATEGORIES.notice;
  return <Badge tone={meta.tone}>{t(meta.label)}</Badge>;
}

/** "Dónde está: Menú › Integraciones › Conectar tienda". */
export function LocationLine({ location }) {
  const { t } = useTranslation();
  const menuLabel = location?.menuPath ? commerceMenuLabelOf(location.menuPath) : null;
  const trail = [menuLabel ? t(menuLabel) : null, location?.detail || null].filter(Boolean);
  if (trail.length === 0) return null;
  return (
    <p className="flex items-start gap-2 rounded-lg bg-bg-muted px-3 py-2 text-sm text-fg-muted">
      <MapPin className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
      <span>
        {t("Dónde está:")}{" "}
        <span className="text-fg">{[t("Menú"), ...trail].join(" › ")}</span>
      </span>
    </p>
  );
}

/** Markdown en línea de un paso, con la misma barrera de links que el cuerpo. */
const STEP_MARKDOWN_COMPONENTS = {
  ...MARKDOWN_COMPONENTS,
  p: ({ children }) => <span>{children}</span>,
};

/**
 * Un paso numerado. La captura se muestra chica y se amplía en el lugar al
 * tocarla: una captura de pantalla achicada no se lee, y un segundo modal
 * encima del detalle pelearía por el foco.
 */
function Step({ step, number }) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const imageUrl = safeImageUrl(step.imageUrl);
  return (
    <li className="flex flex-col gap-2 border-t border-border py-3 first:border-t-0 first:pt-0">
      <div className="flex items-start gap-3">
        <span
          className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-brand-50 text-xs font-semibold text-brand-700"
          aria-hidden="true"
        >
          {number}
        </span>
        <div className="min-w-0 flex-1 leading-relaxed">
          <ReactMarkdown skipHtml urlTransform={urlTransform} components={STEP_MARKDOWN_COMPONENTS}>
            {step.text}
          </ReactMarkdown>
        </div>
        {imageUrl && !expanded && (
          <button
            type="button"
            onClick={() => setExpanded(true)}
            aria-expanded={false}
            aria-label={t("Ampliar la captura del paso {{n}}", { n: number })}
            className="group relative shrink-0 overflow-hidden rounded-md border border-border focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
          >
            <img src={imageUrl} alt="" className="dark-safe-img h-14 w-24 object-cover" />
            <span className="absolute inset-0 flex items-center justify-center bg-black/0 text-white opacity-0 transition-opacity group-hover:bg-black/30 group-hover:opacity-100">
              <Maximize2 className="h-4 w-4" aria-hidden="true" />
            </span>
          </button>
        )}
      </div>
      {imageUrl && expanded && (
        <div className="flex flex-col gap-1 pl-9">
          <img
            src={imageUrl}
            alt={step.imageAlt || ""}
            className="dark-safe-img max-h-[70vh] w-full rounded-lg border border-border object-contain"
          />
          <button
            type="button"
            onClick={() => setExpanded(false)}
            aria-expanded
            className="flex w-fit items-center gap-1 text-xs font-medium text-brand-600 hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
          >
            <Minimize2 className="h-3.5 w-3.5" aria-hidden="true" />
            {t("Achicar captura")}
          </button>
        </div>
      )}
    </li>
  );
}

/**
 * @param {{ news: { category: string, title: string, body?: string,
 *   imageUrl?: string|null, imageAlt?: string|null, date?: string|null,
 *   location?: { menuPath: string|null, detail: string|null }|null,
 *   steps?: { text: string, imageUrl?: string|null, imageAlt?: string|null }[] } }} props
 */
export default function NewsContent({ news }) {
  const { t, i18n } = useTranslation();
  const imageUrl = safeImageUrl(news?.imageUrl);
  const date = formatNewsDate(news?.date, i18n.language);
  const steps = (news?.steps ?? []).filter((step) => step?.text);
  return (
    <article className="flex w-full flex-col gap-3 text-left text-sm text-fg">
      <div className="flex flex-wrap items-center gap-2 text-xs text-fg-muted">
        <CategoryBadge category={news?.category} />
        {date && <time dateTime={news.date}>{date}</time>}
      </div>
      <LocationLine location={news?.location} />
      {imageUrl && (
        <img
          src={imageUrl}
          alt={news?.imageAlt || ""}
          className="dark-safe-img max-h-72 w-full rounded-lg object-contain"
        />
      )}
      {news?.body ? (
        <div className="flex flex-col gap-2">
          <ReactMarkdown skipHtml urlTransform={urlTransform} components={MARKDOWN_COMPONENTS}>
            {news.body}
          </ReactMarkdown>
        </div>
      ) : null}
      {steps.length > 0 && (
        <section aria-label={t("Cómo se hace")} className="flex flex-col gap-1">
          <h3 className="text-sm font-semibold text-fg">{t("Cómo se hace")}</h3>
          <ol className="flex flex-col">
            {steps.map((step, index) => (
              <Step key={index} step={step} number={index + 1} />
            ))}
          </ol>
        </section>
      )}
    </article>
  );
}
